import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash.uniqueid';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import { styleOverrideReason } from '@ids/core';
import {
  MenuItemWrapper,
  MenuItemContainer,
  CheckmarkBox,
  CheckmarkIconWrapper,
  QBCheckmarkIconWrapper
} from './StyledMenu';

const { any, string, func, bool, oneOfType, number, oneOf } = PropTypes;

/** */
export default class MenuItem extends React.PureComponent {
  static contextType = ThemeContext;
  static propTypes = {
    /**
     * Id for component. Assigns a unique id if not provided.
     */
    id: string,
    /**
     * Showing CheckMark on current selected item in Menu
     */
    // eslint-disable-next-line react/boolean-prop-naming
    checkmark: bool,
    /**
     * Children that needs to be displayed, only string.
     */
    // eslint-disable-next-line react/forbid-prop-types
    children: any,
    /**
     * Disable MenuItem if true.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Set if the MenuItem is highlighted or not.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    highlighted: bool,
    /**
     * Callback function to be executed on click.
     */
    onClick: func,
    /**
     * Callback function to be executed on keyUp.
     */
    onKeyUp: func,
    /**
     * Callback function to be executed on keydown.
     */
    onKeyDown: func,
    /**
     * Callback function to be executed on selection
     * Internal use only.
     */
    onSelect: func,
    /**
     * An ARIA role to be assigned to MenuItem.
     */
    role: string,
    /**
     * Set if the MenuItem is selected or not.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    selected: bool,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: oneOf(themes),
    /**
     * Value to be assigned.
     */
    value: oneOfType([string, number])
  };

  static defaultProps = {
    checkmark: false,
    children: undefined,
    disabled: false,
    id: undefined,
    highlighted: false,
    onClick() {},
    onKeyUp() {},
    onKeyDown() {},
    onSelect() {},
    role: 'menuitem',
    selected: false,
    styleOverrideReason: undefined,
    theme: 'intuit',
    value: ''
  };

  constructor(props) {
    super(props);
    this.itemId = uniqueId('idsMenuItem');
    this.itemRef = React.createRef();
    this.state = {
      keyDown: false
    };
  }

  getUpdatedEvent = e => {
    if (e.target) {
      const { target } = e;
      e.persist();
      e.target = {
        ...target,
        value: this.props.value,
        attributes: this.itemRef.current.attributes
      };
    }
  };

  handleClick = event => {
    this.getUpdatedEvent(event);
    this.props.onClick(event);
    this.props.onSelect(event);
  };

  handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.setState({ keyDown: true });
    }

    this.props.onKeyDown(event);
  };

  handleKeyUp = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.state.keyDown) {
        this.getUpdatedEvent(event);
        this.props.onSelect(event);
        this.setState({ keyDown: false });
      }
    }

    this.props.onKeyUp(event);
  };

  render() {
    const {
      checkmark,
      children,
      disabled,
      id,
      highlighted,
      onClick,
      role,
      selected,
      theme,
      ...restProps
    } = this.props;
    const { keyDown } = this.state;
    const isSelected = selected && !checkmark;
    const { currentTheme } = getTheme(this.props, this.context);
    const CheckmarkIcon =
      currentTheme === 'quickbooks'
        ? QBCheckmarkIconWrapper
        : CheckmarkIconWrapper;

    return (
      <MenuItemWrapper
        {...restProps}
        ref={this.itemRef}
        id={id || this.itemId}
        role={role}
        aria-selected={selected || highlighted}
        tabIndex="-1"
        disabled={disabled}
        highlighted={highlighted}
        keyDown={keyDown}
        selected={isSelected}
        theme={currentTheme}
        onClick={disabled ? null : this.handleClick}
        onKeyDown={disabled ? null : this.handleKeyDown}
        onKeyUp={disabled ? null : this.handleKeyUp}
      >
        {checkmark && (
          <CheckmarkBox theme={currentTheme}>
            {selected && <CheckmarkIcon theme={currentTheme} />}
          </CheckmarkBox>
        )}
        <MenuItemContainer
          disabled={disabled}
          keyDown={keyDown}
          selected={isSelected}
          theme={currentTheme}
        >
          {children}
        </MenuItemContainer>
      </MenuItemWrapper>
    );
  }
}
